import { catalog } from './catalog.js';
import { settings } from './storage.js';
import { device } from './device.js';

/* «Preparar al enfocar»: cuando el ratón o el foco del mando se paran un instante sobre un canal, se le pide al proveedor la
   lista HLS del canal (solo la lista, sin vídeo). Casi todos los paneles IPTV tardan uno o dos segundos en «encender» un canal
   la primera vez que alguien lo pide y responden al momento la segunda: así, al pulsar Play, el reproductor se encuentra el
   canal ya encendido. Sobre una película o serie se adelanta su ficha (hace falta antes de reproducir). Nada de esto descarga
   vídeo ni deja conexiones abiertas: la respuesta se cierra nada más llegar. Se puede apagar en Ajustes → Reproducción. */

export const DWELL_MS = 400;        // quieto este rato: pasar deprisa por la lista no pide nada
const AGAIN_MS = 20000;              // el mismo canal no se vuelve a preparar hasta pasado este tiempo
const ABORT_MS = 8000;
const SEGMENT_BYTES = 192 * 1024;   // del primer fragmento se lee solo este trozo (basta para encender el canal y abrir la conexión)

let timer = null;
let ctrl = null;
const warmedAt = new Map();

/** Qué dirección se puede «preparar» sin arrancar el vídeo: solo listas HLS (.m3u8). Un .ts arrancaría el stream. */
export function warmTarget(kind, item, { liveUrl } = {}) {
    if (kind !== 'live' || !item) return '';
    let url = '';
    try { url = String(liveUrl ? liveUrl(item) || '' : ''); } catch (e) { return ''; } // proveedor aún sin cliente: nada que preparar
    return /\.m3u8(\?|#|$)/i.test(url) || /[?&]output=(m3u8|hls)\b/i.test(url) ? url : '';
}

export const warmup = {
    enabled: () => settings.get('prefetchFocus') !== false,
    /** Motor de precarga de verdad (reproductor nativo «beta»): { preload(kind, item, url) → bool, cancel() }. Si acepta, no se hace el GET. */
    engine: null,

    /** Se llama al pasar sobre un canal o un título; espera DWELL_MS y, si el foco sigue ahí, prepara. */
    request(kind, id) {
        this.cancel(false);
        if (!this.enabled()) return;
        timer = setTimeout(() => { timer = null; run(kind, id); }, DWELL_MS);
    },

    /** Al irse a otro sitio: lo pendiente se olvida y la petición en vuelo se corta. */
    cancel(all = true) {
        clearTimeout(timer);
        timer = null;
        if (all && ctrl) { try { ctrl.abort(); } catch (e) { /* ya acabó */ } ctrl = null; }
        if (all && warmup.engine) { try { warmup.engine.cancel(); } catch (e) { /* motor sin cancelar */ } }
    },

    _reset() { warmedAt.clear(); }
};

/** Pide la lista del canal y, si es maestra, la de calidad; después lee un trozo del fragmento por el que empezará hls.js. */
export async function deepWarm(url, signal, withSegment = true) {
    const get = (u) => fetch(u, { credentials: 'omit', cache: 'no-store', signal });
    const r = await get(url);
    if (!r || !r.ok || typeof r.text !== 'function') throw new Error('lista ilegible');
    let text = await r.text();
    let base = r.url || url;
    if (/#EXT-X-STREAM-INF/.test(text)) {
        const lines = text.split(/\r?\n/);
        const i = lines.findIndex((l) => l.indexOf('#EXT-X-STREAM-INF') === 0);
        const uri = lines.slice(i + 1).find((l) => l && l[0] !== '#');
        if (!uri) return;
        const mu = new URL(uri, base).toString();
        const r2 = await get(mu);
        if (!r2 || !r2.ok) return;
        text = await r2.text();
        base = r2.url || mu;
    }
    if (!withSegment) return;
    const segs = text.split(/\r?\n/).filter((l) => l && l[0] !== '#');
    if (!segs.length) return;
    // hls.js arranca unos tres fragmentos antes del final de la lista (liveSyncDurationCount).
    const seg = new URL(segs[Math.max(0, segs.length - 3)], base).toString();
    const r3 = await fetch(seg, { credentials: 'omit', signal });
    if (!r3 || !r3.ok || !r3.body || typeof r3.body.getReader !== 'function') return;
    const reader = r3.body.getReader();
    let got = 0;
    while (got < SEGMENT_BYTES) {
        const { done, value } = await reader.read();
        if (done) break;
        got += value ? value.length : 0;
    }
    try { await reader.cancel(); } catch (e) { /* ya cerrado */ }
}

function run(kind, id) {
    if (kind === 'vod' || kind === 'series') {
        // La ficha hace falta antes de reproducir (y la tarjeta ampliada también la usa): se adelanta y queda en caché.
        if (!catalog.peekInfo(kind, id)) (kind === 'vod' ? catalog.vodInfo(id) : catalog.seriesInfo(id)).catch(() => {});
        return;
    }
    const item = catalog.get(kind, id);
    if (!item) return;
    const key = kind + ':' + id;
    // Con el reproductor beta, el canal se precarga de verdad (lista y primeros segundos) en el reproductor nativo.
    if (warmup.engine) {
        let raw = '';
        try { raw = String(catalog.liveUrl(item) || ''); } catch (e) { raw = ''; }
        let took = false;
        try { took = !!(raw && warmup.engine.preload(kind, item, raw)); } catch (e) { took = false; }
        if (took) return;
    }
    // Un reproductor en marcha no se molesta: la precarga es para cuando todavía no suena nada.
    if (typeof window !== 'undefined' && window.IPTV && window.IPTV.player && window.IPTV.player.isOpen && !window.IPTV.player.minimized) return;
    const url = warmTarget(kind, item, { liveUrl: (ch) => catalog.liveUrl(ch) });
    if (!url) return;
    const last = warmedAt.get(key) || 0;
    if (Date.now() - last < AGAIN_MS) return;
    warmedAt.set(key, Date.now());
    if (typeof fetch !== 'function' || typeof AbortController === 'undefined') return;
    ctrl = new AbortController();
    const mine = ctrl;
    const kill = setTimeout(() => { try { mine.abort(); } catch (e) { /* nada */ } }, ABORT_MS);
    // Con un proveedor que solo admite una conexión, solo la lista (el fragmento ocuparía la única que hay).
    let max = 0;
    try { max = Number((catalog.subscriptionOfSource(item.source || catalog.id) || {}).max) || 0; } catch (e) { max = 0; }
    const sameOrigin = typeof location !== 'undefined' && url.indexOf(location.origin + '/') === 0;
    // Si la respuesta se puede leer (por el proxy de la web o por la app de Windows, que abre el CORS), se sigue por la lista
    // hasta el primer fragmento: lo que se pide es lo que pedirá hls.js, así que el canal arranca con el proveedor ya despierto
    // y la conexión abierta. Si no se puede leer, basta con que la petición llegue (el canal se enciende igual).
    const readable = sameOrigin || (typeof navigator !== 'undefined' && /Electron/i.test(navigator.userAgent || ''));
    const shallow = () => fetch(url, { method: 'GET', mode: sameOrigin ? 'same-origin' : 'no-cors', credentials: 'omit', cache: 'no-store', signal: mine.signal })
        .then((r) => { try { if (r && r.body && r.body.cancel) r.body.cancel(); } catch (e) { /* opaca */ } });
    (readable ? deepWarm(url, mine.signal, max !== 1).catch(() => shallow()) : shallow())
        .catch(() => { warmedAt.delete(key); })
        .finally(() => { clearTimeout(kill); if (ctrl === mine) ctrl = null; });
}

/* Engancha el ratón (escritorio) y el foco del mando (tele) a las filas y tarjetas de canales y a las de películas y series. */
function targetOf(el) {
    const t = el && el.closest ? el.closest('[data-play-live], .card[data-open], .mitem[data-open]') : null;
    if (!t) return null;
    const live = t.getAttribute('data-play-live');
    if (live) return ['live', live];
    const [kind, id] = String(t.getAttribute('data-open') || '').split(':');
    return kind === 'vod' || kind === 'series' ? [kind, id] : null;
}

export function initWarmup() {
    if (typeof document === 'undefined') return;
    const app = document.getElementById('app');
    if (!app) return;
    let lastKey = '';
    const on = (el) => {
        const tg = targetOf(el);
        const key = tg ? tg.join(':') : '';
        if (key === lastKey) return;
        lastKey = key;
        if (tg) warmup.request(tg[0], tg[1]); else warmup.cancel(false);
    };
    if (device.hasHover) app.addEventListener('mouseover', (e) => on(e.target));
    app.addEventListener('nav:focus', (e) => on(e.target));
    document.addEventListener('page:beforeout', () => { lastKey = ''; warmup.cancel(); });
}
